/**
 * Thin wrapper over the portal API. Session lives in an httpOnly cookie, so
 * there is no token for JavaScript to hold (or leak) — every call just needs
 * credentials: 'include'.
 */
async function request(method, path, body) {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })

  let data = null
  try {
    data = await res.json()
  } catch {
    /* empty body */
  }

  if (!res.ok) {
    const err = new Error(data?.error || 'Something went wrong. Please try again.')
    err.status = res.status
    throw err
  }
  return data
}

export const api = {
  get: (p) => request('GET', p),
  post: (p, b) => request('POST', p, b),
  patch: (p, b) => request('PATCH', p, b),
  del: (p) => request('DELETE', p),
}

/* auth */
export const getMe = () => api.get('/auth/me')
export const login = (email, password) => api.post('/auth/login', { email, password })
export const logout = () => api.post('/auth/logout')
export const lookupInvite = (token) => api.get(`/auth/invite/${encodeURIComponent(token)}`)
export const acceptInvite = (token, password) => api.post('/auth/accept-invite', { token, password })
export const changePassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', { currentPassword, newPassword })

/* admin */
export const listUsers = () => api.get('/admin/users')
export const createUser = (payload) => api.post('/admin/users', payload)
export const updateUser = (id, patch) => api.patch(`/admin/users/${id}`, patch)
export const resendInvite = (id) => api.post(`/admin/users/${id}/invite`)
export const signOutUser = (id) => api.post(`/admin/users/${id}/sign-out`)

export const listClients = () => api.get('/admin/clients')
export const createClient = (payload) => api.post('/admin/clients', payload)
export const updateClient = (id, patch) => api.patch(`/admin/clients/${id}`, patch)

export const listInquiries = () => api.get('/admin/inquiries')
export const updateInquiry = (id, patch) => api.patch(`/admin/inquiries/${id}`, patch)
export const addInquiryNote = (id, body) => api.post(`/admin/inquiries/${id}/notes`, { body })

export const listApplications = () => api.get('/admin/applications')
export const updateApplication = (id, patch) => api.patch(`/admin/applications/${id}`, patch)

export const listShifts = () => api.get('/admin/shifts')
export const createShift = (payload) => api.post('/admin/shifts', payload)
export const updateShift = (id, patch) => api.patch(`/admin/shifts/${id}`, patch)
export const deleteShift = (id) => api.del(`/admin/shifts/${id}`)
export const addShiftTask = (id, label) => api.post(`/admin/shifts/${id}/tasks`, { label })
export const deleteShiftTask = (id, taskId) => api.del(`/admin/shifts/${id}/tasks/${taskId}`)
export const createReceipt = (payload) => api.post('/admin/receipts', payload)

/* portals */
export const familyOverview = () => api.get('/portal/family/overview')
export const postFamilyNote = (body) => api.post('/portal/family/notes', { body })
export const caregiverShifts = () => api.get('/portal/caregiver/shifts')
export const checkIn = (id) => api.post(`/portal/caregiver/shifts/${id}/check-in`)
export const checkOut = (id) => api.post(`/portal/caregiver/shifts/${id}/check-out`)
export const toggleTask = (id, done) => api.patch(`/portal/caregiver/tasks/${id}`, { done })
export const saveShiftNote = (id, note) => api.post(`/portal/caregiver/shifts/${id}/note`, { note })

/* public forms */
export const submitInquiry = (payload) => api.post('/public/inquiries', payload)
export const submitApplication = (payload) => api.post('/public/applications', payload)
